// Pure channel-view logic — unit-tested, no Supabase/DOM (the reception
// logic.ts pattern). senderLabel lives in @lemontaigu/staff (shared with the
// S2 analyzer) and is re-exported for the components/tests.
import {
  DEMAND_METRICS,
  TEAM_ORDER,
  todayIsoParis,
  type BoardActivity,
  type DemandDriver,
  type DemandFormula,
  type DemandMetric,
  type StaffMessage,
  type Team,
} from '@lemontaigu/staff';

export { senderLabel } from '@lemontaigu/staff';

/**
 * Calendar day key ('2026-07-12') from a KEY-derived Date (e.g. `new
 * Date('${key}T12:00:00')` inside shiftDay) — noon-anchored arithmetic on an
 * already-local synthetic date, timezone-agnostic by construction. NEVER call
 * this on a live instant (`new Date()` or a real timestamp like sentAt /
 * clock_in) — that depends on the RUNTIME's configured timezone, which is not
 * guaranteed to be Europe/Paris (misconfigured device, CI, browser override).
 * Use `todayIsoParis` for that (it takes any Date, explicit Europe/Paris).
 */
export function dayKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** [start, end) ISO instants of a local calendar day, for the sent_at range query. */
export function dayRange(key: string): { fromIso: string; toIso: string } {
  const start = new Date(`${key}T00:00:00`);
  const end = new Date(start);
  end.setDate(end.getDate() + 1);
  return { fromIso: start.toISOString(), toIso: end.toISOString() };
}

export function shiftDay(key: string, delta: number): string {
  const d = new Date(`${key}T12:00:00`); // noon dodges DST edges
  d.setDate(d.getDate() + delta);
  return dayKey(d);
}

/** Fold a Realtime insert into the loaded window: dedupe by id, keep sent order. */
export function mergeMessage(messages: StaffMessage[], incoming: StaffMessage): StaffMessage[] {
  if (messages.some((m) => m.id === incoming.id)) return messages;
  const next = [...messages, incoming];
  next.sort((a, b) => (a.sentAt < b.sentAt ? -1 : a.sentAt > b.sentAt ? 1 : a.id - b.id));
  return next;
}

/** True when a Realtime insert belongs to the currently displayed Europe/Paris day. */
export function belongsToDay(msg: StaffMessage, key: string): boolean {
  return todayIsoParis(new Date(msg.sentAt)) === key;
}

export function fmtTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

export function fmtDayLabel(key: string): string {
  return new Date(`${key}T12:00:00`).toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
}

// ── Pointages (S3) ───────────────────────────────────────────────────────────

/** First day of the month a day key belongs to ('2026-07-13' → '2026-07-01'). */
export function monthStartKey(key: string): string {
  return `${key.slice(0, 8)}01`;
}

// ── Pointages summary — period navigation (all-employee overview) ────────────

export type SummaryGranularity = 'day' | 'week' | 'month' | 'year';

/** Monday of the week containing `key` (ISO week — Monday start). */
export function weekStartKey(key: string): string {
  const d = new Date(`${key}T12:00:00`);
  const dow = (d.getDay() + 6) % 7; // 0 = Monday
  d.setDate(d.getDate() - dow);
  return dayKey(d);
}

/** Last calendar day of the month containing `key` ('2026-07-13' → '2026-07-31'). */
export function monthEndKey(key: string): string {
  const y = Number(key.slice(0, 4));
  const m = Number(key.slice(5, 7));
  return `${key.slice(0, 7)}-${String(new Date(y, m, 0).getDate()).padStart(2, '0')}`;
}

/** Move a month-anchor by whole months, returning the month-start key. */
export function shiftMonthStartKey(key: string, delta: number): string {
  const d = new Date(Number(key.slice(0, 4)), Number(key.slice(5, 7)) - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

/**
 * The hotel's fiscal year containing `key`: 1 December → 30 November (SAS
 * `entities.md` §1.1). Named by the two calendar years it spans — the year
 * that opens on 2025-12-01 is « Exercice 2025–2026 ».
 */
export function fiscalYearRange(key: string): { fromDay: string; toDay: string; label: string } {
  const y = Number(key.slice(0, 4));
  const start = Number(key.slice(5, 7)) === 12 ? y : y - 1;
  return {
    fromDay: `${start}-12-01`,
    toDay: `${start + 1}-11-30`,
    label: `Exercice ${start}–${start + 1}`,
  };
}

/** Inclusive [fromDay, toDay] + a French label for a granularity anchored on `anchor`.
 *  `fiscalYear` makes « Année » the 1 Dec → 30 Nov fiscal year instead of the
 *  calendar one (payroll stays calendar; the books do not). */
export function summaryRange(
  granularity: SummaryGranularity,
  anchor: string,
  fiscalYear = false,
): { fromDay: string; toDay: string; label: string } {
  if (granularity === 'day') {
    return { fromDay: anchor, toDay: anchor, label: fmtDayLabel(anchor) };
  }
  if (granularity === 'week') {
    const fromDay = weekStartKey(anchor);
    const toDay = shiftDay(fromDay, 6);
    return { fromDay, toDay, label: `Semaine du ${fmtDateShort(fromDay)} au ${fmtDateShort(toDay)}` };
  }
  if (granularity === 'year' && fiscalYear) return fiscalYearRange(anchor);
  if (granularity === 'year') {
    const y = anchor.slice(0, 4);
    return { fromDay: `${y}-01-01`, toDay: `${y}-12-31`, label: `Année ${y}` };
  }
  const fromDay = monthStartKey(anchor);
  const toDay = monthEndKey(anchor);
  const label = new Date(`${fromDay}T12:00:00`).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  return { fromDay, toDay, label };
}

/** Step the anchor forward/back by one unit of the granularity. */
export function shiftSummaryAnchor(
  granularity: SummaryGranularity,
  anchor: string,
  delta: number,
  fiscalYear = false,
): string {
  if (granularity === 'day') return shiftDay(anchor, delta);
  if (granularity === 'week') return shiftDay(weekStartKey(anchor), delta * 7);
  if (granularity === 'year' && fiscalYear) {
    return `${Number(fiscalYearRange(anchor).fromDay.slice(0, 4)) + delta}-12-01`;
  }
  if (granularity === 'year') return `${Number(anchor.slice(0, 4)) + delta}-01-01`;
  return shiftMonthStartKey(anchor, delta);
}

/**
 * The employee's contract coverage across [fromDay, toDay], or null when no
 * period overlaps — i.e. they were NOT employed during the period. Union of
 * overlapping periods (earliest start, latest end; null end = ongoing). Passed
 * to computeTimesheet so « jours de repos » only counts days actually under
 * contract, and used to decide who appears in the period's overview
 * ("then-current", not just currently-current).
 */
export function activeContractWindow(
  contracts: { startDate: string; endDate: string | null }[],
  fromDay: string,
  toDay: string,
): { startDate: string; endDate: string | null } | null {
  const overlapping = contracts.filter(
    (c) => c.startDate <= toDay && (c.endDate == null || c.endDate >= fromDay),
  );
  const first = overlapping[0];
  if (!first) return null;
  let startDate = first.startDate;
  let endDate: string | null = first.endDate;
  let ongoing = first.endDate == null;
  for (const c of overlapping) {
    if (c.startDate < startDate) startDate = c.startDate;
    if (c.endDate == null) ongoing = true;
    else if (endDate != null && c.endDate > endDate) endDate = c.endDate;
  }
  return { startDate, endDate: ongoing ? null : endDate };
}

/** ISO instant → value for an <input type="datetime-local"> (local wall time). */
export function toDatetimeLocal(iso: string): string {
  const d = new Date(iso);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** <input type="datetime-local"> value → ISO instant, or null when empty/invalid. */
export function fromDatetimeLocal(value: string): string | null {
  if (value.trim() === '') return null;
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? new Date(ms).toISOString() : null;
}

/** Decimal hours, French presentation ('7,5 h'; '0,02 h'). */
export function fmtHours(hours: number): string {
  return `${hours.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} h`;
}

// ── Équipe (roster editor, S3 follow-up) ─────────────────────────────────────

/** An employee record as edited in the « Équipe » form. */
export interface EmployeeDraft {
  employeeId: string;
  lastName: string;
  firstName: string;
  email: string;
  phone: string;
  team: '' | Team;
  isSenior: boolean;
  contractStartDate: string;
  contractEndDate: string;
}

/**
 * Client-side guard mirroring staff_upsert_employee (migration 0021) — the SQL
 * is authoritative; this just spares a round-trip and gives a French message.
 * Returns null when the draft is valid.
 */
export function employeeDraftError(d: EmployeeDraft): string | null {
  if (d.employeeId.trim() === '') return 'L’ID Paie est requis.';
  if (d.lastName.trim() === '') return 'Le nom est requis.';
  if (d.firstName.trim() === '') return 'Le prénom est requis.';
  if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(d.email.trim())) return 'Adresse e-mail invalide.';
  if (!/^\+\d{8,15}$/.test(d.phone.trim()))
    return 'Téléphone au format international (ex. +33612345678).';
  if (
    d.contractStartDate !== '' &&
    d.contractEndDate !== '' &&
    d.contractEndDate < d.contractStartDate
  )
    return 'La fin de contrat précède le début.';
  return null;
}

/** French label for a team code (or « — » when unset). */
export function teamLabel(team: '' | Team | null): string {
  switch (team) {
    case 'menage':
      return 'Ménage';
    case 'receptionSalle':
      return 'Réception / Salle';
    case 'cuisine':
      return 'Cuisine';
    default:
      return '—';
  }
}

// ── Registre (contract history — 0023) ───────────────────────────────────────
// The teams in display order — TEAM_ORDER without the « unassigned » slot, so
// Équipe, Activités, Pointages and the board cannot drift apart (2026-08-06).
export const TEAMS: Team[] = TEAM_ORDER.filter((t): t is Team => t !== null);

/**
 * The register read model (ContractPeriod / AdminEmployee / AdminEmployeeRaw /
 * RawContract / rowToAdminEmployee) moved into @lemontaigu/staff at ladder
 * step 6 — RegisterStore returns it, so it cannot live downstream of the port.
 *
 * Re-exported here so this module stays the one import site for every admin
 * view and for logic.test.ts.
 */
import type { AdminEmployee, ContractPeriod } from '@lemontaigu/staff';
import { monthlyHoursFor, STANDARD_WEEKLY_HOURS } from '@lemontaigu/staff';

export type {
  AdminEmployee,
  AdminEmployeeRaw,
  ContractPeriod,
  RawContract,
} from '@lemontaigu/staff';
export { rowToAdminEmployee } from '@lemontaigu/staff';

/**
 * Seniority key of a person: the RUP hire_rank of their FIRST period when
 * imported, else their earliest start date. Lower = more senior. People with
 * no contract sort last.
 */
export function seniorityKey(e: AdminEmployee): { rank: number | null; start: string | null } {
  const ranks = e.contracts.map((c) => c.hireRank).filter((r): r is number => r != null);
  return { rank: ranks.length ? Math.min(...ranks) : null, start: e.firstStartDate };
}

/**
 * Assign a 1-based « rang » (hire order) across the WHOLE roster, so the number
 * is stable regardless of the active filter. Ordered by hire_rank (if imported)
 * then earliest start date; unranked/undated people come last.
 */
export function rankByHireOrder(all: AdminEmployee[]): Map<string, number> {
  const ordered = [...all].sort(compareSeniority);
  const map = new Map<string, number>();
  ordered.forEach((e, i) => {
    const { rank, start } = seniorityKey(e);
    map.set(e.id, rank == null && start == null ? 0 : i + 1);
  });
  return map;
}

/** Most-senior-first comparator (hire_rank, then start date, then name). */
export function compareSeniority(a: AdminEmployee, b: AdminEmployee): number {
  const ka = seniorityKey(a);
  const kb = seniorityKey(b);
  if (ka.rank != null && kb.rank != null && ka.rank !== kb.rank) return ka.rank - kb.rank;
  if (ka.rank != null && kb.rank == null) return -1;
  if (ka.rank == null && kb.rank != null) return 1;
  if (ka.start && kb.start && ka.start !== kb.start) return ka.start < kb.start ? -1 : 1;
  if (ka.start && !kb.start) return -1;
  if (!ka.start && kb.start) return 1;
  return compareName(a, b);
}

/** Alphabetical by first name then last name (the register's other sort). */
export function compareName(a: AdminEmployee, b: AdminEmployee): number {
  return (
    a.firstName.localeCompare(b.firstName, 'fr') || a.lastName.localeCompare(b.lastName, 'fr')
  );
}

export type StatusFilter = 'all' | 'current' | 'past';
export type TeamFilter = 'all' | Team | 'none';
export type SortKey = 'team' | 'rank' | 'name' | 'end';

/** Filter the roster by employment status, team, and a free-text query. */
export function filterEmployees(
  all: AdminEmployee[],
  opts: { status: StatusFilter; team: TeamFilter; q: string },
): AdminEmployee[] {
  const q = opts.q.trim().toLowerCase();
  return all.filter((e) => {
    if (opts.status === 'current' && !e.isCurrent) return false;
    if (opts.status === 'past' && e.isCurrent) return false;
    if (opts.team === 'none' && e.team !== '') return false;
    if (opts.team !== 'all' && opts.team !== 'none' && e.team !== opts.team) return false;
    if (q) {
      const hay = `${e.firstName} ${e.lastName} ${e.employeeId} ${e.email} ${e.phone} ${e.nickname ?? ''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
}

/**
 * By contract end date, soonest first (who leaves next — CDD season
 * planning); open-ended contracts (CDI, end null) last; ties by name.
 */
export function compareEndDate(a: AdminEmployee, b: AdminEmployee): number {
  if (a.contractEndDate === b.contractEndDate) return compareName(a, b);
  if (a.contractEndDate === null) return 1;
  if (b.contractEndDate === null) return -1;
  return a.contractEndDate < b.contractEndDate ? -1 : 1;
}

/**
 * The latest contract period of a person — the one whose type and end date
 * describe their CURRENT employment (periods are appended, never rewritten).
 */
function latestContract(e: AdminEmployee): ContractPeriod | null {
  return e.contracts.reduce<ContractPeriod | null>(
    (latest, c) => (latest === null || c.startDate > latest.startDate ? c : latest),
    null,
  );
}

/**
 * Contract-type rank: CDI first, CDD last, everything else (Saisonnier, Extra,
 * Apprentissage…) between them. Permanence is the axis — the operator reads
 * this list to see who is settled and who is on a clock.
 */
function contractTypeRank(type: string | null): number {
  if (type === 'CDI') return 0;
  if (type === 'CDD') return 2;
  return 1;
}

/**
 * « Trier par équipe » (operator default, 2026-08-06): team in TEAMS order,
 * then people currently en poste before ancien·ne·s, then CDI → other → CDD,
 * then the contract end date DESCENDING — within one team the person whose
 * contract runs longest sits at the top, and whoever leaves first sits nearest
 * the ancien·ne·s below them. An open-ended contract (no end date) has no
 * horizon at all, so it leads.
 */
export function compareTeamRoster(a: AdminEmployee, b: AdminEmployee): number {
  const teamRank = (e: AdminEmployee): number => {
    const i = TEAMS.indexOf(e.team as Team);
    return i === -1 ? TEAMS.length : i; // « sans équipe » last
  };
  if (teamRank(a) !== teamRank(b)) return teamRank(a) - teamRank(b);
  if (a.isCurrent !== b.isCurrent) return a.isCurrent ? -1 : 1;

  const ca = latestContract(a);
  const cb = latestContract(b);
  const typeDelta = contractTypeRank(ca?.contractType ?? null) - contractTypeRank(cb?.contractType ?? null);
  if (typeDelta !== 0) return typeDelta;

  // End date descending; null (open-ended) first.
  const ea = ca?.endDate ?? null;
  const eb = cb?.endDate ?? null;
  if (ea !== eb) {
    if (ea === null) return -1;
    if (eb === null) return 1;
    return ea > eb ? -1 : 1;
  }
  return compareName(a, b);
}

/** Sort a (filtered) roster for display. */
export function sortEmployees(rows: AdminEmployee[], key: SortKey): AdminEmployee[] {
  return [...rows].sort(
    key === 'team'
      ? compareTeamRoster
      : key === 'rank'
        ? compareSeniority
        : key === 'end'
          ? compareEndDate
          : compareName,
  );
}

/** Group current employees by team for the Équipe grid (unassigned last). */
export function groupByTeam(rows: AdminEmployee[]): { team: '' | Team; members: AdminEmployee[] }[] {
  const groups: { team: '' | Team; members: AdminEmployee[] }[] = [];
  for (const team of TEAMS) {
    const members = rows.filter((e) => e.team === team).sort(compareSeniority);
    if (members.length) groups.push({ team, members });
  }
  const unassigned = rows.filter((e) => e.team === '').sort(compareSeniority);
  if (unassigned.length) groups.push({ team: '', members: unassigned });
  return groups;
}

/** '2026-07-14' → '14 juil. 2026' (compact, for contract chips). */
export function fmtDateShort(iso: string | null): string {
  if (!iso) return '—';
  return new Date(`${iso}T12:00:00`).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/** Human range of a period: '14 juil. 2026 → en cours' / '… → 30 sept. 2026'. */
export function contractRange(c: Pick<ContractPeriod, 'startDate' | 'endDate'>): string {
  return `${fmtDateShort(c.startDate)} → ${c.endDate ? fmtDateShort(c.endDate) : 'en cours'}`;
}

/** A single contract as edited in the Registre detail panel. */
export interface ContractDraft {
  contractType: string;
  contractNumber: string;
  startDate: string;
  endDate: string;
  note: string;
  emploi: string;
  qualification: string;
  pcsEse: string;
  statutConventionnel: string;
  niveau: string;
  echelon: string;
  coef: string;
  brutHoraire: string;
  brutMensuel: string;
  weeklyHours: string;
  tempsPartiel: boolean;
  motifSortie: string;
  authEntree: string;
  authSortie: string;
  docType: string;
  docNumber: string;
  apprenti: boolean;
  contratQualification: boolean;
  contratAdaptation: boolean;
}

export function emptyContractDraft(): ContractDraft {
  return {
    contractType: 'CDD', contractNumber: '', startDate: '', endDate: '', note: '',
    emploi: '', qualification: '', pcsEse: '', statutConventionnel: '', niveau: '', echelon: '',
    coef: '', brutHoraire: '', brutMensuel: '', weeklyHours: '', tempsPartiel: false, motifSortie: '',
    authEntree: '', authSortie: '', docType: '', docNumber: '',
    apprenti: false, contratQualification: false, contratAdaptation: false,
  };
}

/** Existing period → editable draft. When `clone`, drop the identity of the
 * period (id/dates/rank) but keep the reusable TERMS — the rehire workflow. */
/** Round a stored salary figure to 2 decimals for display (some legacy imports carry raw division noise, e.g. 12.197534120129288). */
const roundMoney = (n: number): string => String(Math.round(n * 100) / 100);

export function contractToDraft(c: ContractPeriod, clone = false): ContractDraft {
  return {
    contractType: c.contractType ?? '',
    contractNumber: clone ? '' : c.contractNumber ?? '', // a new hiring gets a fresh DSN number
    startDate: clone ? '' : c.startDate,
    endDate: clone ? '' : c.endDate ?? '',
    note: clone ? '' : c.note ?? '',
    emploi: c.emploi ?? '',
    qualification: c.qualification ?? '',
    pcsEse: c.pcsEse ?? '',
    statutConventionnel: c.statutConventionnel ?? '',
    niveau: c.niveau ?? '',
    echelon: c.echelon ?? '',
    coef: c.coef ?? '',
    brutHoraire: c.brutHoraire == null ? '' : roundMoney(c.brutHoraire),
    brutMensuel: c.brutMensuel == null ? '' : roundMoney(c.brutMensuel),
    weeklyHours: c.weeklyHours == null ? '' : String(c.weeklyHours),
    tempsPartiel: c.tempsPartiel,
    motifSortie: clone ? '' : c.motifSortie ?? '',
    authEntree: clone ? '' : c.authEntree ?? '',
    authSortie: clone ? '' : c.authSortie ?? '',
    docType: c.docType ?? '',
    docNumber: clone ? '' : c.docNumber ?? '',
    apprenti: c.apprenti,
    contratQualification: c.contratQualification,
    contratAdaptation: c.contratAdaptation,
  };
}

const numOrNull = (s: string): number | null => {
  const n = Number(s.replace(',', '.'));
  return s.trim() !== '' && Number.isFinite(n) ? n : null;
};
const strOrNull = (s: string): string | null => (s.trim() === '' ? null : s.trim());

/** ContractDraft → staff_admin_upsert_contract params. */
export function contractParams(d: ContractDraft, employeeId: string, id: string | null) {
  return {
    p_id: id,
    p_employee_id: employeeId,
    p_contract_type: strOrNull(d.contractType),
    p_contract_number: strOrNull(d.contractNumber),
    p_start_date: d.startDate,
    p_end_date: strOrNull(d.endDate),
    p_hire_rank: null, // numéro d'ordre is auto-assigned server-side (0027)
    p_note: strOrNull(d.note),
    p_emploi: strOrNull(d.emploi),
    p_qualification: strOrNull(d.qualification),
    p_pcs_ese: strOrNull(d.pcsEse),
    p_statut_conventionnel: strOrNull(d.statutConventionnel),
    p_niveau: strOrNull(d.niveau),
    p_echelon: strOrNull(d.echelon),
    p_coef: strOrNull(d.coef),
    p_brut_horaire: numOrNull(d.brutHoraire),
    p_brut_mensuel: numOrNull(d.brutMensuel),
    p_weekly_hours: numOrNull(d.weeklyHours),
    p_temps_partiel: d.tempsPartiel,
    p_motif_sortie: strOrNull(d.motifSortie),
    p_auth_entree: strOrNull(d.authEntree),
    p_auth_sortie: strOrNull(d.authSortie),
    p_doc_type: strOrNull(d.docType),
    p_doc_number: strOrNull(d.docNumber),
    p_apprenti: d.apprenti,
    p_contrat_qualification: d.contratQualification,
    p_contrat_adaptation: d.contratAdaptation,
  };
}

/**
 * Derive the brut horaire from the brut mensuel, using the legal full-time
 * monthly base (35 h/week → 151.67 h/month) and rounding to the nearest 0.05 €
 * — e.g. 2200 → 14.50, 2124 → 14.00 (matches the operator's convention). Returns
 * null for a non-positive/invalid mensuel.
 */
// 151,67 — DERIVED, not a literal: monthlyHoursFor(35) in @lemontaigu/staff owns
// the 52 ÷ 12 conversion. Kept as a re-export so existing callers are untouched
// while the fact has exactly one home.
export const FULL_TIME_MONTHLY_HOURS = monthlyHoursFor(STANDARD_WEEKLY_HOURS);
export function brutHoraireFromMensuel(mensuel: number): number | null {
  if (!Number.isFinite(mensuel) || mensuel <= 0) return null;
  const raw = mensuel / FULL_TIME_MONTHLY_HOURS;
  return Math.round((Math.round(raw / 0.05) * 0.05) * 100) / 100;
}

/** Client guard mirroring staff_admin_upsert_contract (SQL is authoritative). */
export function contractDraftError(d: ContractDraft): string | null {
  if (d.startDate.trim() === '') return 'La date de début est requise.';
  if (d.endDate !== '' && d.endDate < d.startDate) return 'La fin précède le début.';
  if (d.brutHoraire !== '' && numOrNull(d.brutHoraire) === null) return 'Brut horaire invalide.';
  if (d.brutMensuel !== '' && numOrNull(d.brutMensuel) === null) return 'Brut mensuel invalide.';
  if (d.weeklyHours !== '') {
    const h = numOrNull(d.weeklyHours);
    // Mirrors the column CHECK (> 0 and <= 60) so the operator sees the reason
    // here rather than a raw constraint violation from PostgREST.
    if (h === null || h <= 0 || h > 60) return 'Heures hebdomadaires invalides (entre 1 et 60).';
  }
  return null;
}

/**
 * A contract's end date / motif de sortie are editable only while it is the
 * LAST hire FOR THAT EMPLOYEE — not the register's global numéro d'ordre;
 * each employee has their own contract history — AND still current
 * (register-integrity rule, 0027; scoping corrected 2026-07-19 — comparing
 * against the register-wide max hire_rank meant only the single most-
 * recently-hired person company-wide could ever edit their current
 * contract). Note + N° de contrat stay editable regardless. NOT used for
 * deletion — see contractDeletable, a stricter, register-wide check.
 */
export function contractMutable(c: ContractPeriod, employeeContracts: ContractPeriod[], asOf: string): boolean {
  const current = c.endDate == null || c.endDate >= asOf;
  const employeeMaxRank = employeeContracts.reduce((max, x) => (x.hireRank != null && x.hireRank > max ? x.hireRank : max), 0);
  // closedAt is the source of truth for "terminated" (migration 20260811) —
  // once closed, mutability ends regardless of end_date. Mirrors
  // staff_contract_mutable in SQL; that copy is authoritative.
  return c.hireRank != null && c.hireRank === employeeMaxRank && employeeMaxRank > 0 && current && c.closedAt == null;
}

/**
 * "Terminated" reads ONLY closedAt (migration 20260811) — motifSortie is
 * descriptive text, not a state flag. A contract can have an end date and
 * even a motif typed into an unsaved form field without ever being closed;
 * that gap (found 2026-08-10) is exactly what closedAt exists to close.
 */
export function contractTerminated(c: Pick<ContractPeriod, 'closedAt'>): boolean {
  return c.closedAt != null;
}

/** Every distinct motif already used anywhere in the register, most-recent-close first — the "Clôturer" picklist. Free entry is always still allowed alongside it. */
export function usedMotifs(all: AdminEmployee[]): string[] {
  const seen = new Map<string, string>(); // lowercase → original casing, first occurrence wins
  const byRecentClose = all
    .flatMap((e) => e.contracts)
    .filter((c) => c.motifSortie != null && c.motifSortie.trim() !== '')
    .sort((a, b) => (b.closedAt ?? '').localeCompare(a.closedAt ?? ''));
  for (const c of byRecentClose) {
    const key = c.motifSortie!.trim().toLowerCase();
    if (!seen.has(key)) seen.set(key, c.motifSortie!.trim());
  }
  return [...seen.values()];
}

/** ContractDraft-independent: the "Clôturer" dialog's own tiny params → staff_admin_close_contract. */
export function closeContractParams(contractId: string, closedAt: string, motifSortie: string) {
  return { p_id: contractId, p_closed_at: closedAt, p_motif_sortie: strOrNull(motifSortie) };
}

/** Client guard mirroring staff_admin_close_contract (SQL is authoritative). */
export function closeContractError(c: Pick<ContractPeriod, 'endDate'>, closedAt: string, motifSortie: string): string | null {
  if (c.endDate == null) return 'La date de fin doit être renseignée avant de clôturer.';
  if (closedAt.trim() === '') return 'La date de clôture est requise.';
  if (c.endDate > closedAt) return 'La date de fin du contrat est postérieure à la date de clôture.';
  if (motifSortie.trim() === '') return 'Le motif de sortie est requis pour clôturer.';
  return null;
}

/** The highest numéro d'ordre across the whole register. */
export function registerMaxHireRank(all: AdminEmployee[]): number {
  let max = 0;
  for (const e of all) for (const c of e.contracts) if (c.hireRank != null && c.hireRank > max) max = c.hireRank;
  return max;
}

/**
 * A contract is deletable only if it holds the register's globally-highest
 * numéro d'ordre — deleting anything else would leave a gap in the RUP's
 * required 1, 2, 3, … sequence (operator requirement 2026-07-19). Deliberately
 * NOT scoped per-employee like contractMutable: an employee's own last hire
 * can still be mid-sequence if someone else was hired more recently.
 */
export function contractDeletable(c: ContractPeriod, registerMaxRank: number): boolean {
  return c.hireRank != null && c.hireRank === registerMaxRank && registerMaxRank > 0;
}

// ── Person identity draft (full RUP identity) ────────────────────────────────
export interface PersonDraft extends EmployeeDraft {
  birthName: string;
  address: string;
  ssn: string;
  nationality: string;
  birthDate: string;
  sex: '' | 'F' | 'M';
  nickname: string;
}

export function emptyPersonDraft(): PersonDraft {
  return {
    employeeId: '', lastName: '', firstName: '', email: '', phone: '', team: '', isSenior: false,
    contractStartDate: '', contractEndDate: '',
    birthName: '', address: '', ssn: '', nationality: '', birthDate: '', sex: '', nickname: '',
  };
}

export function personToDraft(e: AdminEmployee): PersonDraft {
  return {
    employeeId: e.employeeId, lastName: e.lastName, firstName: e.firstName,
    email: e.email ?? '', phone: e.phone ?? '', team: e.team, isSenior: e.isSenior,
    contractStartDate: '', contractEndDate: '',
    birthName: e.birthName ?? '', address: e.address ?? '', ssn: e.ssn ?? '',
    nationality: e.nationality ?? '', birthDate: e.birthDate ?? '', sex: (e.sex as '' | 'F' | 'M') ?? '',
    nickname: e.nickname ?? '',
  };
}

/** PersonDraft → staff_admin_upsert_person params. Email/phone are optional now. */
export function personParams(d: PersonDraft, id: string | null) {
  return {
    p_id: id,
    p_employee_id: d.employeeId.trim(),
    p_last_name: d.lastName.trim(),
    p_first_name: d.firstName.trim(),
    p_email: strOrNull(d.email)?.toLowerCase() ?? null,
    p_phone: strOrNull(d.phone),
    p_team: d.team === '' ? null : d.team,
    p_is_senior: d.isSenior,
    p_birth_name: strOrNull(d.birthName),
    p_address: strOrNull(d.address),
    p_ssn: strOrNull(d.ssn),
    p_nationality: strOrNull(d.nationality),
    p_birth_date: strOrNull(d.birthDate),
    p_sex: d.sex === '' ? null : d.sex,
    p_nickname: strOrNull(d.nickname),
  };
}

/** Client guard for the person form — identity now, contracts separate. */
export function personDraftError(d: PersonDraft): string | null {
  if (d.employeeId.trim() === '') return 'L’ID Paie est requis.';
  if (d.lastName.trim() === '') return 'Le nom est requis.';
  if (d.firstName.trim() === '') return 'Le prénom est requis.';
  if (d.email.trim() !== '' && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(d.email.trim()))
    return 'Adresse e-mail invalide.';
  if (d.phone.trim() !== '' && !/^\+\d{8,15}$/.test(d.phone.trim()))
    return 'Téléphone au format international (ex. +33612345678).';
  return null;
}

// ── RUP regeneration — derived columns (never stored) ────────────────────────

/** RUP « Nom complet » = « NOM Prénom ». */
export function nomComplet(e: Pick<AdminEmployee, 'lastName' | 'firstName'>): string {
  return `${e.lastName} ${e.firstName}`.trim();
}

/** Calendar Y/M/D between two ISO days (end exclusive-of-nothing: inclusive span). */
export function calendarDiff(startIso: string, endIso: string): { y: number; m: number; d: number } {
  let y = Number(endIso.slice(0, 4)) - Number(startIso.slice(0, 4));
  let m = Number(endIso.slice(5, 7)) - Number(startIso.slice(5, 7));
  let d = Number(endIso.slice(8, 10)) - Number(startIso.slice(8, 10));
  if (d < 0) {
    m -= 1;
    // days in the month preceding the end month
    const em = Number(endIso.slice(5, 7));
    const ey = Number(endIso.slice(0, 4));
    const prevMonthDays = new Date(ey, em - 1, 0).getDate();
    d += prevMonthDays;
  }
  if (m < 0) {
    y -= 1;
    m += 12;
  }
  return { y, m, d };
}

/** RUP « Ancienneté » — '6y 4m 4d' / '3m 5d' / '10d' (matches the register). */
export function anciennete(startIso: string, endIso: string | null, asOf: string): string {
  const end = endIso ?? asOf;
  if (end < startIso) return '';
  const { y, m, d } = calendarDiff(startIso, end);
  if (y > 0) return `${y}y ${m}m ${d}d`;
  if (m > 0) return `${m}m ${d}d`;
  return `${d}d`;
}

/** RUP « Présent » — the period covers `asOf` (today). */
export function isPresent(c: Pick<ContractPeriod, 'startDate' | 'endDate'>, asOf: string): boolean {
  return c.startDate <= asOf && (c.endDate == null || c.endDate >= asOf);
}

/** Start of the trailing 12-month window ending `asOf` ('2026-07-14' → '2025-07-14'). */
export function rollingWindowStart(asOf: string): string {
  const y = Number(asOf.slice(0, 4)) - 1;
  return `${y}${asOf.slice(4)}`;
}

/** RUP « Présent sur 12 mois » — the period overlaps [asOf−12m, asOf]. */
export function isPresent12m(c: Pick<ContractPeriod, 'startDate' | 'endDate'>, asOf: string): boolean {
  const from = rollingWindowStart(asOf);
  return c.startDate <= asOf && (c.endDate == null || c.endDate >= from);
}

/** One flattened RUP row = person identity + one contract + derived columns. */
export interface RupRow {
  person: AdminEmployee;
  contract: ContractPeriod;
  nomComplet: string;
  anciennete: string;
  present: boolean;
  present12m: boolean;
}

/** Flatten the roster into RUP rows (one per contract), ordered by numéro
 * d'ordre (hire_rank) then start date — the register's canonical order. */
export function flattenRup(all: AdminEmployee[], asOf: string): RupRow[] {
  const rows: RupRow[] = [];
  for (const person of all) {
    for (const contract of person.contracts) {
      rows.push({
        person,
        contract,
        nomComplet: nomComplet(person),
        anciennete: anciennete(contract.startDate, contract.endDate, asOf),
        present: isPresent(contract, asOf),
        present12m: isPresent12m(contract, asOf),
      });
    }
  }
  rows.sort((a, b) => {
    const ra = a.contract.hireRank;
    const rb = b.contract.hireRank;
    if (ra != null && rb != null && ra !== rb) return ra - rb;
    if (ra != null && rb == null) return -1;
    if (ra == null && rb != null) return 1;
    return a.contract.startDate < b.contract.startDate ? -1 : 1;
  });
  return rows;
}

/** '2026-07-14' → '14/07/2026' (RUP date format). */
export function fmtRupDate(iso: string | null): string {
  if (!iso) return '';
  return `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;
}

export type RupSortKey = 'ordre' | 'end';

/**
 * Filter RUP rows by employment status and a free-text query (name / ID Paie /
 * e-mail). Same StatusFilter/labels as the Registre (« Contrats ») view — « En
 * poste » there is isCurrent, here it is r.present (isPresent(contract, asOf)):
 * both mean "covers today", just computed on a different read model.
 */
export function filterRupRows(rows: RupRow[], opts: { status: StatusFilter; q: string }): RupRow[] {
  const q = opts.q.trim().toLowerCase();
  return rows.filter((r) => {
    if (opts.status === 'current' && !r.present) return false;
    if (opts.status === 'past' && r.present) return false;
    if (q) {
      const hay = `${r.nomComplet} ${r.person.employeeId} ${r.person.email ?? ''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
}

/**
 * Sort RUP rows. 'ordre' is the register's legal order (flattenRup's own
 * hire_rank / start_date ordering — the default, and what the printed
 * document expects). 'end' surfaces who leaves next (CDD season planning);
 * open-ended periods (no `endDate`) sort last, ties by hire order.
 */
export function sortRupRows(rows: RupRow[], key: RupSortKey): RupRow[] {
  if (key === 'ordre') return rows;
  return [...rows].sort((a, b) => {
    const ea = a.contract.endDate;
    const eb = b.contract.endDate;
    if (ea === eb) return 0;
    if (ea === null) return 1;
    if (eb === null) return -1;
    return ea < eb ? -1 : 1;
  });
}

/** CSS modifier for a contract-type badge — CDI/CDD get their own colour, everything else is neutral. */
export function contractTypeClass(type: string | null | undefined): string {
  if (type === 'CDI') return 'contract-type-cdi';
  if (type === 'CDD') return 'contract-type-cdd';
  return 'contract-type-other';
}

// ── Planning (day-board activity matrix — S4-core, migration 0032) ──────────

/**
 * How an activity's demand is decided. Since v3 there are only two modes:
 * « Toujours » (an anchor, needed every day) or « Selon la charge » (a
 * weighted sum of base metrics). The old per-metric dropdown collapsed into
 * the second mode — a single metric is just a formula with one coefficient
 * of 1. « ancre » was dropped from the label: staff did not recognise it.
 */
export const DRIVERS: { value: DemandDriver; label: string }[] = [
  { value: 'always', label: 'Toujours' },
  { value: 'covers', label: 'Selon la charge' },
];

export function driverLabel(driver: DemandDriver): string {
  return driver === 'always' ? 'Toujours' : 'Selon la charge';
}

export interface ActivityDraft {
  label: string;
  /** 'HH:MM' (the <input type="time"> value). */
  windowStart: string;
  windowEnd: string;
  windowNote: string;
  demandDriver: DemandDriver;
  /**
   * Weighted sum over base metrics, as typed ('' = coefficient 0). Only
   * meaningful when demandDriver !== 'always'.
   */
  demandFormula: Partial<Record<DemandMetric, string>>;
  /**
   * « Seuil » — minimum driver value before a dedicated person is needed
   * (Plonge: 20 couverts). '' = no threshold, i.e. required on any demand.
   * Not applicable to the 'always' anchors.
   */
  minDemand: string;
  /**
   * Registry references (ADR-0015). '' = none — for `loadMetricKey` that means
   * fall back to the legacy `demandFormula`, for `badgeMetricKey` it means no
   * badge. Kept as strings because they are `<select>` values.
   */
  loadMetricKey: string;
  badgeMetricKey: string;
  active: boolean;
  sort: string;
  /** Gantt display group (0034); '' = unassigned (« Autres »). */
  team: '' | Team;
  /** « Sans horaire » — a sign-up activity with no window; hides the time inputs and skips window validation. */
  untimed: boolean;
}

export function emptyActivityDraft(nextSort: number): ActivityDraft {
  return {
    label: '',
    windowStart: '',
    windowEnd: '',
    windowNote: '',
    demandDriver: 'always',
    demandFormula: {},
    minDemand: '',
    loadMetricKey: '',
    badgeMetricKey: '',
    active: true,
    sort: String(nextSort),
    team: '',
    untimed: false,
  };
}

/** Postgres time 'HH:MM:SS' → the 'HH:MM' an <input type="time"> expects. */
export function timeInputValue(time: string): string {
  return time.slice(0, 5);
}

export function activityToDraft(a: BoardActivity): ActivityDraft {
  return {
    label: a.label,
    windowStart: timeInputValue(a.windowStart),
    windowEnd: timeInputValue(a.windowEnd),
    windowNote: a.windowNote ?? '',
    demandDriver: a.demandDriver,
    demandFormula: Object.fromEntries(
      DEMAND_METRICS.filter((m) => a.demandFormula?.[m] !== undefined).map((m) => [
        m,
        String(a.demandFormula?.[m]),
      ]),
    ),
    minDemand: a.minDemand === null ? '' : String(a.minDemand),
    loadMetricKey: a.loadMetricKey ?? '',
    badgeMetricKey: a.badgeMetricKey ?? '',
    active: a.active,
    sort: String(a.sort),
    team: a.team ?? '',
    untimed: a.untimed,
  };
}

/**
 * Client-side guard mirroring the 0032 checks — the SQL is authoritative;
 * this spares a round-trip and gives a French message. Null = valid.
 */
export function activityDraftError(d: ActivityDraft): string | null {
  if (d.label.trim() === '') return 'Le libellé est requis.';
  // Untimed activities (« sans horaire ») carry no window — skip the time checks.
  if (!d.untimed) {
    if (!/^\d{2}:\d{2}$/.test(d.windowStart) || !/^\d{2}:\d{2}$/.test(d.windowEnd))
      return 'Les horaires de début et de fin sont requis.';
    if (d.windowEnd <= d.windowStart) return 'La fin de la plage précède son début.';
  }
  if (d.sort.trim() !== '' && !/^-?\d+$/.test(d.sort.trim())) return 'L’ordre doit être un nombre entier.';
  const seuil = d.minDemand.trim();
  if (seuil !== '') {
    if (!/^\d+$/.test(seuil)) return 'Le seuil doit être un nombre entier.';
    if (Number(seuil) < 1) return 'Le seuil doit être au moins 1.';
  }
  if (d.demandDriver !== 'always') {
    for (const metric of DEMAND_METRICS) {
      const raw = (d.demandFormula[metric] ?? '').trim();
      if (raw === '') continue;
      // Accept '1', '0.5' and the French '0,5' — the desk types a comma.
      if (!/^-?\d+([.,]\d+)?$/.test(raw)) return 'Les coefficients doivent être des nombres.';
    }
    // Since ADR-0015 the load may come from the REGISTRY instead of the legacy
    // inline formula. Requiring both would make it impossible to point an
    // activity at a metric without also keeping a redundant second definition —
    // which is precisely the duplication the registry removes.
    if (d.loadMetricKey === '' && Object.keys(demandFormulaOf(d)).length === 0)
      return 'Choisissez une métrique de charge (onglet Métriques) pour « Selon la charge ».';
  }
  return null;
}

/** Parse a coefficient, tolerating the French decimal comma. */
function coefficient(raw: string | undefined): number | null {
  const v = (raw ?? '').trim().replace(',', '.');
  if (v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) && n !== 0 ? n : null;
}

/**
 * Draft → the jsonb formula actually stored. Empty and zero coefficients are
 * dropped rather than written as 0: an absent metric already means 0, and
 * storing the zeroes would make every formula an eight-key object that reads
 * as noise in the DB and in `formatFormula`.
 */
export function demandFormulaOf(d: ActivityDraft): DemandFormula {
  const formula: DemandFormula = {};
  for (const metric of DEMAND_METRICS) {
    const c = coefficient(d.demandFormula[metric]);
    if (c !== null) formula[metric] = c;
  }
  return formula;
}
